import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Gift, Check, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const peso = (v) => `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META = {
  pending_approval: { label: 'Awaiting your approval', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved · added to payment', cls: 'bg-blue-100 text-blue-700' },
  awaiting_payment: { label: 'Awaiting payment', cls: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid · releases on completion', cls: 'bg-indigo-100 text-indigo-700' },
  settled: { label: 'Paid', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Declined', cls: 'bg-red-100 text-red-600' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500' },
};

const BookingCharges = ({ booking, effectiveRole }) => {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Provider add-on form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addDesc, setAddDesc] = useState('');
  const [addAmount, setAddAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Client tip
  const [showTip, setShowTip] = useState(false);
  const [tipAmount, setTipAmount] = useState('');
  const [payMethod, setPayMethod] = useState('gcash');

  const isProvider = effectiveRole === 'provider';
  const isClient = effectiveRole === 'client';
  const basePaid = booking?.payment_status === 'paid';
  // Provider can propose add-ons once the booking is accepted (even before the
  // client pays the base). The client can only Approve & Pay once the base is paid.
  const bookingActive = ['accepted', 'in_progress', 'completion_requested', 'completed'].includes(booking?.status);

  const fetchCharges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings/${booking.id}/charges`);
      setCharges(res.data.charges || []);
    } catch (e) {
      /* non-blocking */
    } finally {
      setLoading(false);
    }
  }, [booking.id]);

  useEffect(() => { fetchCharges(); }, [fetchCharges]);

  const flash = (setter, text) => { setter(text); setTimeout(() => setter(''), 4000); };

  const handleCheckoutResult = (data) => {
    if (data.checkout_url) {
      window.location.href = data.checkout_url; // live PayMongo
      return;
    }
    flash(setMsg, 'Payment completed.'); // sandbox
    fetchCharges();
  };

  const submitAddon = async () => {
    setErr('');
    const amt = parseFloat(addAmount);
    if (!addDesc.trim() || !amt || amt <= 0) { flash(setErr, 'Enter a description and a valid amount.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/charges`, { description: addDesc.trim(), amount: amt });
      setAddDesc(''); setAddAmount(''); setShowAddForm(false);
      flash(setMsg, 'Charge sent to the client for approval.');
      fetchCharges();
    } catch (e) {
      flash(setErr, e.response?.data?.detail || 'Failed to add charge.');
    } finally { setSubmitting(false); }
  };

  const approve = async (id) => {
    setBusyId(id); setErr('');
    try {
      const res = await axios.post(`${API}/charges/${id}/approve`, { payment_method: payMethod });
      if (res.data?.bundled) {
        flash(setMsg, 'Approved! This amount will be added to your “Pay Now” total.');
        fetchCharges();
      } else {
        handleCheckoutResult(res.data);
      }
    } catch (e) {
      flash(setErr, e.response?.data?.detail || 'Failed to approve.');
    } finally { setBusyId(null); }
  };

  const reject = async (id) => {
    setBusyId(id);
    try { await axios.post(`${API}/charges/${id}/reject`); flash(setMsg, 'Charge declined.'); fetchCharges(); }
    catch (e) { flash(setErr, e.response?.data?.detail || 'Failed.'); }
    finally { setBusyId(null); }
  };

  const cancel = async (id) => {
    setBusyId(id);
    try { await axios.post(`${API}/charges/${id}/cancel`); fetchCharges(); }
    catch (e) { flash(setErr, e.response?.data?.detail || 'Failed.'); }
    finally { setBusyId(null); }
  };

  const sendTip = async () => {
    setErr('');
    const amt = parseFloat(tipAmount);
    if (!amt || amt <= 0) { flash(setErr, 'Enter a valid tip amount.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings/${booking.id}/tip`, { amount: amt, payment_method: payMethod });
      setTipAmount(''); setShowTip(false);
      handleCheckoutResult(res.data);
    } catch (e) {
      flash(setErr, e.response?.data?.detail || 'Failed to send tip.');
    } finally { setSubmitting(false); }
  };

  // Only relevant once a provider is assigned (booking accepted+). For not-yet-accepted
  // bookings, show a discoverable hint so users know the feature exists.
  if (!bookingActive) {
    return (
      <div className="bg-gray-50 rounded-xl p-4 border border-dashed border-gray-300" data-testid="booking-charges-locked">
        <h4 className="font-semibold text-gray-700 mb-1">Extra Charges & Tips</h4>
        <p className="text-sm text-gray-500">
          Once this booking is accepted, the provider can request charges for extra work and the client can send a tip here.
        </p>
      </div>
    );
  }

  const addons = charges.filter((c) => c.kind === 'addon');
  const tips = charges.filter((c) => c.kind === 'tip');

  const renderCharge = (c) => {
    const meta = STATUS_META[c.status] || { label: c.status, cls: 'bg-gray-100 text-gray-600' };
    return (
      <div key={c.id} className="bg-white rounded-lg p-3 border" data-testid={`charge-${c.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">
              {c.kind === 'tip' ? 'Tip' : (c.description || 'Additional charge')}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {isProvider ? `You receive ${peso(c.provider_payout_amount)}` : `You pay ${peso(c.amount)}`}
              {c.kind === 'addon' && c.client_fee ? ` · incl. fee ${peso(c.client_fee)}` : ''}
            </p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${meta.cls}`} data-testid={`charge-status-${c.id}`}>{meta.label}</span>
        </div>

        {/* Client actions on pending add-ons */}
        {isClient && c.kind === 'addon' && c.status === 'pending_approval' && (
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => approve(c.id)}
              disabled={busyId === c.id}
              className="flex-1 inline-flex items-center justify-center gap-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`approve-charge-${c.id}`}
            >
              {busyId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {basePaid ? 'Approve & Pay' : 'Approve'}
            </button>
            <button
              onClick={() => reject(c.id)}
              disabled={busyId === c.id}
              className="flex-1 inline-flex items-center justify-center gap-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              data-testid={`reject-charge-${c.id}`}
            >
              <X className="w-4 h-4" /> Decline
            </button>
          </div>
        )}
        {/* Provider can cancel only while still awaiting the client's approval (locked once approved) */}
        {isProvider && c.kind === 'addon' && c.status === 'pending_approval' && (
          <button
            onClick={() => cancel(c.id)}
            disabled={busyId === c.id}
            className="mt-2 text-xs text-red-500 hover:text-red-600"
            data-testid={`cancel-charge-${c.id}`}
          >
            Cancel this charge
          </button>
        )}
        {/* Client can pay an approved-but-unpaid charge (separate checkout, base already paid) */}
        {isClient && c.kind === 'addon' && ['approved', 'awaiting_payment'].includes(c.status) && c.checkout_url && (
          <a href={c.checkout_url} className="mt-2 inline-block text-sm text-blue-600 font-medium" data-testid={`pay-charge-${c.id}`}>
            Continue payment →
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="booking-charges-section">
      {msg && <div className="text-sm bg-green-100 text-green-700 rounded-lg px-3 py-2" data-testid="charges-msg">{msg}</div>}
      {err && <div className="text-sm bg-red-100 text-red-700 rounded-lg px-3 py-2" data-testid="charges-err">{err}</div>}

      {/* ===== EXTRA CHARGES ===== */}
      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-testid="extra-charges-block">
        <h4 className="font-semibold text-indigo-800 mb-3">Extra Charges</h4>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : addons.length === 0 ? (
          <p className="text-sm text-gray-500 mb-3">No extra charges yet.</p>
        ) : (
          <div className="space-y-2 mb-3">{addons.map(renderCharge)}</div>
        )}

        {/* Bundling note for client when base isn't paid yet */}
        {isClient && !basePaid && addons.some((c) => c.status === 'approved') && (
          <div className="mb-3 text-xs text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-lg px-3 py-2" data-testid="charges-bundled-note">
            Approved charges are added to your “Pay Now” total — you’ll pay the booking and extras together in one payment.
          </div>
        )}
        {isClient && !basePaid && addons.some((c) => c.status === 'pending_approval') && (
          <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="charges-await-base">
            Approve a charge to add it to your booking payment, or decline it.
          </div>
        )}

        {/* Payment method selector for the separate add-on checkout (base already paid) */}
        {isClient && basePaid && addons.some((c) => c.status === 'pending_approval') && (
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">Payment method</label>
            <select
              value={payMethod}
              onChange={(e) => setPayMethod(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
              data-testid="charge-payment-method"
            >
              <option value="gcash">GCash</option>
              <option value="qrph">QR Ph</option>
              <option value="maya">Maya</option>
              <option value="card">Credit/Debit Card</option>
            </select>
          </div>
        )}

        {/* Provider: add an extra charge */}
        {isProvider && (
          showAddForm ? (
            <div className="bg-white rounded-lg p-3 border space-y-2" data-testid="addon-form">
              <input
                type="text" value={addDesc} onChange={(e) => setAddDesc(e.target.value)}
                placeholder="What is this charge for? (e.g. extra materials)"
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="addon-desc"
              />
              <input
                type="number" min="1" step="0.01" value={addAmount} onChange={(e) => setAddAmount(e.target.value)}
                placeholder="Amount (₱) for the extra work"
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="addon-amount"
              />
              <p className="text-xs text-gray-400">The client pays this plus their platform fee. You receive it minus the provider fee.</p>
              <div className="flex gap-2">
                <button onClick={submitAddon} disabled={submitting}
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                  data-testid="addon-submit">
                  {submitting ? 'Sending…' : 'Send for approval'}
                </button>
                <button onClick={() => { setShowAddForm(false); setErr(''); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddForm(true)}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-indigo-300 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100"
              data-testid="add-charge-btn">
              <Plus className="w-4 h-4" /> Request additional charge
            </button>
          )
        )}
      </div>

      {/* ===== TIPS ===== */}
      <div className="bg-pink-50 rounded-xl p-4 border border-pink-100" data-testid="tips-block">
        <h4 className="font-semibold text-pink-800 mb-3">Tips</h4>

        {tips.length === 0 ? (
          <p className="text-sm text-gray-500 mb-3">No tips yet.</p>
        ) : (
          <div className="space-y-2 mb-3">{tips.map(renderCharge)}</div>
        )}

        {isClient && !basePaid && (
          <p className="text-xs text-gray-500" data-testid="tip-locked-note">You can send a tip after the booking is paid.</p>
        )}

        {isClient && basePaid && (
          showTip ? (
            <div className="bg-white rounded-lg p-3 border space-y-2" data-testid="tip-form">
              <input
                type="number" min="1" step="0.01" value={tipAmount} onChange={(e) => setTipAmount(e.target.value)}
                placeholder="Tip amount (₱)"
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="tip-amount"
              />
              <div>
                <label className="block text-xs text-gray-500 mb-1">Payment method</label>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                  data-testid="tip-payment-method"
                >
                  <option value="gcash">GCash</option>
                  <option value="qrph">QR Ph</option>
                  <option value="maya">Maya</option>
                  <option value="card">Credit/Debit Card</option>
                </select>
              </div>
              <p className="text-xs text-gray-400">Tips are optional and go 100% to your provider — no platform fee.</p>
              <div className="flex gap-2">
                <button onClick={sendTip} disabled={submitting}
                  className="flex-1 py-2 bg-pink-600 text-white rounded-lg text-sm font-medium hover:bg-pink-700 disabled:opacity-50"
                  data-testid="tip-submit">
                  {submitting ? 'Processing…' : 'Send tip'}
                </button>
                <button onClick={() => { setShowTip(false); setErr(''); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowTip(true)}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-pink-300 text-pink-700 rounded-lg text-sm font-medium hover:bg-pink-100"
              data-testid="send-tip-btn">
              <Gift className="w-4 h-4" /> Send a tip (optional)
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default BookingCharges;
